import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { VdsField } from '@volt/volt-extended-components-react';
import { useTranslation } from 'react-i18next';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import DropdownMultiselect from '@cas/components/inputs/dropdown-multiselect/DropdownMultiselect';
import { useCallback } from 'react';

export interface CasFormMultiselectProps<TFormValues extends FieldValues, TValue>
  extends UseControllerProps<TFormValues> {
  label?: string;
  placeholder?: string;
  isReadonly?: boolean;
  maxHeight?: number;
  useItemsHook: () => DropdownItemProps<TValue>[];
  displaySelectionLimit?: number;
  enableSelectAll?: boolean;
}

export default function CasFormMultiselect<TFormValues extends FieldValues, TValue>(
  props: CasFormMultiselectProps<TFormValues, TValue>,
) {
  const { t } = useTranslation();

  const { disabled, ...sanitisedProps } = props;
  const { field, fieldState, formState } = useController(sanitisedProps);

  const {
    name,
    placeholder,
    label,
    isReadonly,
    maxHeight,
    useItemsHook,
    enableSelectAll = false,
  } = props;

  const items = useItemsHook();

  const isError = !(props.disabled || field.disabled) && fieldState.error !== undefined;
  const errorMessage = isError ? fieldState.error?.message : undefined;
  const isDisabled = props.disabled || field.disabled || formState.isSubmitting;

  const onChange = useCallback(
    (newValue: TValue[]) => {
      field.onChange(newValue);
    },
    [field],
  );

  return (
    <VdsField key={name} label={label ?? ''} errorMessage={errorMessage} isDisabled={isDisabled}>
      <DropdownMultiselect
        uid={name}
        items={items}
        onChange={onChange}
        placeholder={placeholder ?? t('Select')}
        error={isError}
        disabled={isDisabled}
        isReadonly={isReadonly}
        value={field.value}
        onBlur={field.onBlur}
        maxHeight={`${maxHeight ?? 300}px`}
        displaySelectionLimit={props.displaySelectionLimit}
        enableSelectAll={enableSelectAll}
      />
    </VdsField>
  );
}
